/**
 * @lineup-lite/components
 * https://github.com/sgratzl/lineup-lite
 *
 * Copyright (c) 2021 Samuel Gratzl <sam@sgratzl.com>
 */

import React, { useMemo, useRef, useCallback, useState, useLayoutEffect } from 'react';
import boxplot from '@sgratzl/boxplots';

const EMPTY_ARR = [];
function toPercent(v) {
    return `${Math.round(v * 1000) / 10}%`;
}
function clsx(...args) {
    return args.filter(Boolean).join(' ');
}
function mergeStyles(...args) {
    const s = args.filter((d) => Boolean(d));
    if (s.length <= 1) {
        return s[0];
    }
    return Object.assign({}, ...s);
}
function format(value, formatter) {
    return typeof formatter === 'string' ? formatter : typeof formatter === 'function' ? formatter(value) : String(value);
}
function i18n(pattern, ...args) {
    if (args.length === 0) {
        return pattern;
    }
    return pattern.replace(/\{(\d+)\}/g, (_, v) => { var _a; return String((_a = args[Number.parseInt(v, 10)]) !== null && _a !== void 0 ? _a : '?'); });
}
function toLocaleString(v) {
    return v == null ? '' : v.toLocaleString();
}
function useI18N(original, overrides) {
    return useMemo(() => {
        const r = {};
        for (const key of Object.keys(original)) {
            const v = (overrides && overrides[key] != null ? overrides[key] : original[key]);
            r[key] = i18n.bind(null, v);
        }
        return r;
    }, [original, overrides]);
}

function NumberStatsWrapper(props) {
    const hasCenter = props.s.center != null && props.summary;
    return (React.createElement("div", { className: clsx(props.className, 'lt-summary', !props.summary && 'lt-group', hasCenter && 'lt-summary-center'), "data-min": props.s.min != null && props.summary ? props.s.format(props.s.min) : null, "data-max": props.s.max != null && props.summary ? props.s.format(props.s.max) : null, style: props.style },
        hasCenter && props.s.center != null && React.createElement("div", { className: "lt-summary-center" }, props.s.format(props.s.center)),
        props.children));
}

const FILTER_RANGE_I18N_EN = {
    filterRangeMinFilter: 'Min Filter: {0}',
    filterRangeMaxFilter: 'Max Filter: {0}',
};
function FilterRangeSlider(props) {
    const { setFilter } = props;
    const { invert, scale } = props.s;
    const { filterValue } = props;
    const [localFilter, setLocalFilter] = useState(filterValue !== null && filterValue !== void 0 ? filterValue : [null, null]);
    const filterRef = useRef(localFilter);
    filterRef.current = localFilter;
    const i18n = useI18N(FILTER_RANGE_I18N_EN, props.i18n);
    useLayoutEffect(() => {
        const v = filterRef.current;
        const l = filterValue !== null && filterValue !== void 0 ? filterValue : [null, null];
        if (v[0] !== l[0] || v[1] !== l[1]) {
            setLocalFilter(l);
        }
    }, [setLocalFilter, filterRef, filterValue]);
    const onMinMouseDown = useMemo(() => {
        let ueber = null;
        let base = 0;
        let width = 0;
        const onMinMouseMove = (evt) => {
            const ratio = Math.min(1, Math.max(0, (evt.clientX - base) / width));
            const v = [ratio <= 0 ? null : invert(ratio), filterRef.current[1]];
            setLocalFilter(v);
            setFilter(v[0] == null && v[1] == null ? undefined : v);
        };
        const onMinMouseUp = () => {
            if (!ueber) {
                return;
            }
            ueber.removeEventListener('mousemove', onMinMouseMove);
            ueber.removeEventListener('mouseleave', onMinMouseUp);
            ueber.removeEventListener('mouseup', onMinMouseUp);
            ueber = null;
        };
        return (evt) => {
            const bb = evt.currentTarget.parentElement.parentElement.getBoundingClientRect();
            base = bb.x;
            width = bb.width;
            ueber = evt.currentTarget.closest('body');
            ueber.addEventListener('mousemove', onMinMouseMove);
            ueber.addEventListener('mouseleave', onMinMouseUp);
            ueber.addEventListener('mouseup', onMinMouseUp);
        };
    }, [setFilter, setLocalFilter, filterRef, invert]);
    const onMaxMouseDown = useMemo(() => {
        let ueber = null;
        let base = 0;
        let width = 0;
        const onMinMouseMove = (evt) => {
            const ratio = Math.min(1, Math.max(0, (evt.clientX - base) / width));
            const v = [filterRef.current[0], ratio >= 1 ? null : invert(ratio)];
            setLocalFilter(v);
            setFilter(v[0] == null && v[1] == null ? undefined : v);
        };
        const onMinMouseUp = () => {
            if (!ueber) {
                return;
            }
            ueber.removeEventListener('mousemove', onMinMouseMove);
            ueber.removeEventListener('mouseleave', onMinMouseUp);
            ueber.removeEventListener('mouseup', onMinMouseUp);
            ueber = null;
        };
        return (evt) => {
            const bb = evt.currentTarget.parentElement.parentElement.getBoundingClientRect();
            base = bb.x;
            width = bb.width;
            ueber = evt.currentTarget.closest('body');
            ueber.addEventListener('mousemove', onMinMouseMove);
            ueber.addEventListener('mouseleave', onMinMouseUp);
            ueber.addEventListener('mouseup', onMinMouseUp);
        };
    }, [setFilter, setLocalFilter, filterRef, invert]);
    const clearFilter = useCallback(() => {
        setLocalFilter([null, null]);
        setFilter(undefined);
    }, [setFilter, setLocalFilter]);
    const setShortCutFilter = useCallback((evt) => {
        const bb = evt.currentTarget.getBoundingClientRect();
        const ratio = Math.min(1, Math.max(0, (evt.clientX - bb.x) / bb.width));
        const currentMinDistance = Math.abs(ratio - (typeof filterRef.current[0] === 'number' ? scale(filterRef.current[0]) : 0));
        const currentMaxDistance = Math.abs(ratio - (typeof filterRef.current[1] === 'number' ? scale(filterRef.current[1]) : 1));
        const useMin = currentMinDistance <= currentMaxDistance;
        const domainValue = invert(ratio);
        if (useMin) {
            setLocalFilter([domainValue, filterRef.current[1]]);
            setFilter([domainValue, filterRef.current[1]]);
        }
        else {
            setLocalFilter([filterRef.current[0], domainValue]);
            setFilter([filterRef.current[0], domainValue]);
        }
    }, [setFilter, setLocalFilter, filterRef, invert, scale]);
    props.refData.current = {
        clearFilter,
        setShortCutFilter,
    };
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: "lt-filter-range lt-filter-range-min", "data-value": localFilter[0], title: localFilter[0] == null ? undefined : i18n.filterRangeMinFilter(props.s.format(localFilter[0])), style: { width: toPercent(localFilter[0] == null ? 0 : Math.max(0, props.s.scale(localFilter[0]))) } },
            React.createElement("div", { className: "lt-filter-range-drag", onMouseDown: onMinMouseDown, role: "presentation" })),
        React.createElement("div", { className: "lt-filter-range lt-filter-range-max", "data-value": localFilter[1], title: localFilter[1] == null ? undefined : i18n.filterRangeMaxFilter(props.s.format(localFilter[1])), style: { width: toPercent(localFilter[1] == null ? 0 : Math.max(0, 1 - props.s.scale(localFilter[1]))) } },
            React.createElement("div", { className: "lt-filter-range-drag", onMouseDown: onMaxMouseDown, role: "presentation" }))));
}
function FilterRangeWrapper(props) {
    const refData = useRef({ clearFilter() { }, setShortCutFilter() { } });
    const clearFilter = useCallback(() => refData.current.clearFilter(), [refData]);
    const setShortCutFilter = useCallback((evt) => refData.current.setShortCutFilter(evt), [refData]);
    const hasCenter = props.s.center != null && props.summary;
    return (React.createElement("div", { className: clsx('lt-summary', !props.summary && 'lt-group', hasCenter && 'lt-summary-center', props.className), "data-min": props.s.min != null && props.summary ? props.s.format(props.s.min) : null, "data-max": props.s.max != null && props.summary ? props.s.format(props.s.max) : null, onDoubleClick: clearFilter, onClick: setShortCutFilter, role: "presentation", style: props.style, "data-filtered": props.filterValue ? 'true' : undefined },
        hasCenter && props.s.center != null && React.createElement("div", { className: "lt-summary-center" }, props.s.format(props.s.center)),
        props.children,
        React.createElement(FilterRangeSlider, Object.assign({}, props, { refData: refData }))));
}

const BOXPLOT_I18N_EN = {
    boxplotMinimum: 'Minimum: {0}',
    boxplot25Quantile: '25% Quantile: {0}',
    boxplotMedian: 'Median: {0}',
    boxplotMean: 'Mean: {0}',
    boxplot75Quantile: '75% Quantile: {0}',
    boxplotMaximum: 'Maximum: {0}',
    boxplotNrItems: '# Items: {0}',
};
function generatePath(stats, s, h, padding, hor = true) {
    const cy = h / 2;
    const q1 = s(stats.q1);
    const q3 = s(stats.q3);
    const boxHeight = h - padding * 2;
    const whiskerLow = hor
        ? `M ${s(stats.whiskerLow)} 0 l 0 ${h} l 0 -${cy} L ${q1} ${cy}`
        : `M 0 ${s(stats.whiskerLow)} l ${h} 0 l -${cy} 0 L ${cy} ${q1}`;
    const whiskerHigh = hor
        ? `M ${s(stats.whiskerHigh)} 0 l 0 ${h} l 0 -${cy} L ${q3} ${cy}`
        : `M 0 ${s(stats.whiskerHigh)} l ${h} 0 l -${cy} 0 L ${cy} ${q3}`;
    const median = hor
        ? `M ${s(stats.median)} ${padding} l 0 ${boxHeight}`
        : `M ${padding} ${s(stats.median)} l ${boxHeight} 0`;
    return `${whiskerLow} ${whiskerHigh} ${median}`;
}
function generateTitle(s, pre, i18n) {
    const p = (v) => `/${s.format(v)}`;
    return `${i18n.boxplotMinimum(`${s.format(s.min)}${pre ? p(pre.min) : ''}`)}
${i18n.boxplot25Quantile(`${s.format(s.q1)}${pre ? p(pre.q1) : ''}`)}
${i18n.boxplotMedian(`${s.format(s.median)}${pre ? p(pre.median) : ''}`)}
${i18n.boxplotMean(`${s.format(s.mean)}${pre ? p(pre.mean) : ''}`)}
${i18n.boxplot75Quantile(`${s.format(s.q3)}${pre ? p(pre.q3) : ''}`)}
${i18n.boxplotMaximum(`${s.format(s.max)}${pre ? p(pre.max) : ''}`)}
${i18n.boxplotNrItems(`${s.count.toLocaleString()}${pre ? `/${pre.count.toLocaleString()}` : ''}`)}`;
}
function BoxPlotChart(props) {
    const { s, preFilter: pre } = props;
    const i18n = useI18N(BOXPLOT_I18N_EN, props.i18n);
    const boxPadding = 2;
    const scale = useCallback((v) => Math.round(s.scale(v) * 1000) / 10, [s]);
    const outlierRadius = 4;
    const height = 20;
    const generateBoxPlot = (b) => {
        if (Number.isNaN(b.median)) {
            return null;
        }
        const cy = height / 2;
        const path = generatePath(b, scale, height, boxPadding);
        return (React.createElement(React.Fragment, null,
            React.createElement("rect", { x: scale(b.q1), y: boxPadding, height: height - boxPadding * 2, width: scale(b.median) - scale(b.q1), className: "lt-boxplot-box", style: { fill: s.color(s.scale((b.median + b.q1) / 2)) } }),
            React.createElement("rect", { x: scale(b.median), y: boxPadding, height: height - boxPadding * 2, width: scale(b.q3) - scale(b.median), style: { fill: s.color(s.scale((b.median + b.q3) / 2)) }, className: "lt-boxplot-box" }),
            React.createElement("path", { d: `M${scale(b.mean)} ${boxPadding} l 0 ${height - boxPadding * 2}`, className: "lt-boxplot-mean" }),
            React.createElement("path", { d: path, className: "lt-boxplot-frame" }),
            b.outlier.map((o) => (React.createElement("path", { className: "lt-boxplot-outlier", key: o, d: `M${scale(o)} ${cy - outlierRadius} l 0 ${2 * outlierRadius}` },
                React.createElement("title", null, s.format(o)))))));
    };
    const offset = pre != null ? height * 0.1 : 0;
    return (React.createElement("svg", { viewBox: `0 0 100 ${height + offset}`, className: props.className, style: props.style, preserveAspectRatio: "none" },
        React.createElement("title", null, generateTitle(s, pre, i18n)),
        pre != null && pre.count > s.count && (React.createElement("g", { className: "lt-boxplot-pre", transform: `translate(0,${offset})` }, generateBoxPlot(pre))),
        generateBoxPlot(s)));
}
function BoxPlotChartVertical(props) {
    const { s } = props;
    const pre = props.preFilter;
    const i18n = useI18N(BOXPLOT_I18N_EN, props.i18n);
    const boxPadding = 2;
    const scale = useCallback((v) => Math.round(s.scale(v) * 1000) / 10, [s]);
    const outlierRadius = 4;
    const width = 20;
    const generateBoxPlot = (b) => {
        if (Number.isNaN(b.median)) {
            return null;
        }
        const cx = width / 2;
        const path = generatePath(b, scale, width, boxPadding, false);
        return (React.createElement(React.Fragment, null,
            React.createElement("rect", { y: scale(b.q1), x: boxPadding, width: width - boxPadding * 2, height: scale(b.median) - scale(b.q1), className: "lt-boxplot-box", style: { fill: s.color(s.scale((b.median + b.q1) / 2)) } }),
            React.createElement("rect", { y: scale(b.median), x: boxPadding, width: width - boxPadding * 2, height: scale(b.q3) - scale(b.median), style: { fill: s.color(s.scale((b.median + b.q3) / 2)) }, className: "lt-boxplot-box" }),
            React.createElement("path", { d: `M ${boxPadding} ${scale(b.mean)} l ${width - boxPadding * 2} 0`, className: "lt-boxplot-mean" }),
            React.createElement("path", { d: path, className: "lt-boxplot-frame" }),
            b.outlier.map((o) => (React.createElement("path", { className: "lt-boxplot-outlier", key: o, d: `M ${cx - outlierRadius} ${scale(o)} l ${2 * outlierRadius} 0` },
                React.createElement("title", null, s.format(o)))))));
    };
    const offset = pre != null ? width * 0.1 : 0;
    return (React.createElement("svg", { viewBox: `0 0 ${width + offset} 100`, className: props.className, style: props.style, preserveAspectRatio: "none" },
        React.createElement("title", null, generateTitle(s, pre, i18n)),
        pre != null && pre.count > s.count && (React.createElement("g", { className: "lt-boxplot-pre", transform: `translate(${offset},0)` }, generateBoxPlot(pre))),
        generateBoxPlot(s)));
}
function BoxPlot(props) {
    return (React.createElement(NumberStatsWrapper, { className: clsx('lt-boxplot', props.className), s: props.s, summary: props.summary, style: props.style },
        React.createElement(BoxPlotChart, Object.assign({}, props, { className: clsx('lt-boxplot-wrapper', props.className) }))));
}
function FilterRangeBoxPlot(props) {
    return (React.createElement(FilterRangeWrapper, Object.assign({ summary: props.summary }, props, { className: clsx('lt-boxplot', props.className) }),
        React.createElement(BoxPlotChart, Object.assign({}, props, { className: clsx('lt-boxplot-wrapper', props.className) }))));
}

function CategoricalColor(props) {
    const title = format(props.value, props.format);
    return (React.createElement("div", { className: clsx('lt-categorical', props.className), style: mergeStyles(props.style, props.value != null
            ? {
                borderLeftColor: typeof props.color === 'string' ? props.color : props.color(props.value),
            }
            : null), title: title }, title));
}

function DateLabel(props) {
    var _a;
    const label = format(props.value, (_a = props.format) !== null && _a !== void 0 ? _a : toLocaleString);
    return (React.createElement("div", { className: clsx('lt-date', props.className), style: props.style, title: label }, label));
}

const DENSE = 10;
function generateBinTitle(h, raw, title) {
    const rawT = raw ? `/${raw.count.toLocaleString()}` : '';
    const base = `${h.label}: ${h.count.toLocaleString()}${rawT}`;
    if (title) {
        return i18n(title, base);
    }
    return base;
}
function computeBinGradient(h, maxBin, preFilter, i) {
    const raw = preFilter === null || preFilter === void 0 ? void 0 : preFilter.hist[i];
    const p = toPercent(h.count / maxBin);
    if (maxBin <= 0 || (h.count > 0 && h.count >= maxBin)) {
        return { backgroundColor: h.color };
    }
    if (raw && raw.count > h.count) {
        const semi = `rgba(var(--current-inverted-color-rgb, 255,255,255), 0.7)`;
        const rawP = toPercent(raw.count / maxBin);
        if (h.count > 0) {
            return {
                backgroundImage: `linear-gradient(to top, ${h.color} ${p}, ${semi} ${p}, ${semi} ${rawP}, transparent ${rawP}), linear-gradient(to top, ${h.color} ${rawP}, transparent ${rawP})`,
            };
        }
        return {
            backgroundImage: `linear-gradient(to top, ${semi} ${rawP}, transparent ${rawP}), linear-gradient(to top, ${h.color} ${rawP}, transparent ${rawP})`,
        };
    }
    if (h.count <= 0) {
        return undefined;
    }
    return { backgroundImage: `linear-gradient(to top, ${h.color} ${p}, transparent ${p})` };
}
function Histogram(props) {
    return (React.createElement(NumberStatsWrapper, { style: props.style, className: clsx('lt-histogram', props.className), s: props.s, summary: props.summary }, props.s.hist.map((h, i) => (React.createElement(Bin, { key: String(h.x0), h: h, i: i, parentProps: props })))));
}
function Bin({ h, parentProps, i, onClick, title, filtered, }) {
    var _a, _b;
    const { preFilter } = parentProps;
    const maxBin = (_b = (_a = parentProps.maxBin) !== null && _a !== void 0 ? _a : preFilter === null || preFilter === void 0 ? void 0 : preFilter.maxBin.count) !== null && _b !== void 0 ? _b : parentProps.s.maxBin.count;
    const lastBin = parentProps.s.hist.length - 1;
    const dense = parentProps.s.hist.length > DENSE || i === lastBin;
    const style = computeBinGradient(h, maxBin, preFilter, i);
    return (React.createElement("div", { className: clsx('lt-histogram-bin', dense && 'lt-histogram-bin-dense', onClick && 'lt-histogram-bin-interactive'), style: style, "data-i": i, onClick: onClick, "data-label": parentProps.label ? h.label : null, title: generateBinTitle(h, preFilter === null || preFilter === void 0 ? void 0 : preFilter.hist[i], title), "data-filtered": filtered, role: "presentation" }));
}
const FILTER_BIN_I18N_EN = {
    filterBin: '{0} - Click to filter this out this bin',
    removeFilterBin: '{0} - Click to remove filtering out this bin',
};
function FilterBinHistogram(props) {
    const { setFilter, filterValue = EMPTY_ARR } = props;
    const { hist } = props.s;
    const i18n = useMemo(() => {
        var _a;
        return ({
            ...FILTER_BIN_I18N_EN,
            ...((_a = props.i18n) !== null && _a !== void 0 ? _a : {}),
        });
    }, [props.i18n]);
    const onClick = useCallback((evt) => {
        var _a;
        const current = filterValue !== null && filterValue !== void 0 ? filterValue : EMPTY_ARR;
        const bin = hist[Number.parseInt((_a = evt.currentTarget.dataset.i) !== null && _a !== void 0 ? _a : '0', 10)];
        const value = bin.x0;
        const next = current.includes(value) ? current.filter((d) => d !== value) : current.concat([value]);
        setFilter(next.length === 0 ? undefined : next);
    }, [setFilter, hist, filterValue]);
    return (React.createElement(NumberStatsWrapper, { style: props.style, className: clsx('lt-histogram', props.className), s: props.s, summary: true }, props.s.hist.map((h, i) => (React.createElement(Bin, { key: String(h.x0), h: h, parentProps: props, i: i, onClick: onClick, filtered: (filterValue !== null && filterValue !== void 0 ? filterValue : EMPTY_ARR).includes(h.x0) ? 'true' : undefined, title: (filterValue !== null && filterValue !== void 0 ? filterValue : EMPTY_ARR).includes(h.x0) ? i18n.removeFilterBin : i18n.filterBin })))));
}
function FilterRangeHistogram(props) {
    return (React.createElement(FilterRangeWrapper, Object.assign({ summary: true }, props, { className: clsx('lt-histogram', props.className) }), props.s.hist.map((h, i) => (React.createElement(Bin, { key: String(h.x0), h: h, i: i, parentProps: props })))));
}
const FILTER_SET_I18N_EN = {
    filterMustNotSet: '{0} - Click to must not have this set',
    filterMustSet: '{0} - Click to must have this set',
    filterMaybeSet: '{0} - Click to remove filtering by this set',
};
function FilterSetHistogram(props) {
    const { setFilter, filterValue = EMPTY_ARR } = props;
    const { hist } = props.s;
    const i18n = useMemo(() => {
        var _a;
        return ({
            ...FILTER_SET_I18N_EN,
            ...((_a = props.i18n) !== null && _a !== void 0 ? _a : {}),
        });
    }, [props.i18n]);
    const onClick = useCallback((evt) => {
        var _a;
        const current = filterValue !== null && filterValue !== void 0 ? filterValue : EMPTY_ARR;
        const bin = hist[Number.parseInt((_a = evt.currentTarget.dataset.i) !== null && _a !== void 0 ? _a : '0', 10)];
        const value = bin.x0;
        const index = current.findIndex((d) => d.set === value);
        if (index < 0) {
            setFilter(current.concat([{ set: value, value: true }]));
        }
        else if (current[index].value) {
            current[index].value = false;
            setFilter(current.slice());
        }
        else {
            const next = current.slice();
            next.splice(index, 1);
            setFilter(next.length === 0 ? undefined : next);
        }
    }, [setFilter, hist, filterValue]);
    return (React.createElement(NumberStatsWrapper, { style: props.style, className: clsx('lt-histogram', props.className), s: props.s, summary: true }, props.s.hist.map((h, i) => {
        const index = (filterValue !== null && filterValue !== void 0 ? filterValue : EMPTY_ARR).findIndex((d) => d.set === h.x0);
        return (React.createElement(Bin, { key: String(h.x0), h: h, parentProps: props, i: i, onClick: onClick, filtered: index < 0 ? undefined : filterValue[index].value ? 'must' : 'mustNot', title: index < 0 ? i18n.filterMustSet : filterValue[index].value ? i18n.filterMustNotSet : i18n.filterMaybeSet }));
    })));
}

function defaultScale(v) {
    return Math.max(Math.min(v, 1), 0);
}
function defaultConstantColorScale() {
    return 'rgba(0,0,0,0.25)';
}
function defaultConstantDarkColorScale() {
    return 'rgba(255,255,255,0.25)';
}
function invertColorScale(scale) {
    return (v) => scale(1 - v);
}
function defaultColorScale(v) {
    const maxL = 0.9;
    const minL = 0.23;
    return `hsl(206, 64%, ${Math.round(100 * (v * (maxL - minL) + minL))}%)`;
}
function defaultColorScale2(v) {
    const maxL = 0.9;
    const minL = 0.23;
    return `hsl(20, 64%, ${Math.round(100 * (v * (maxL - minL) + minL))}%)`;
}
function defaultDivergingColorScale(v) {
    if (v >= 0.5) {
        return defaultColorScale((v - 0.5) * 2);
    }
    return defaultColorScale2(1 - v * 2);
}
const defaultDarkColorScale = invertColorScale(defaultColorScale);
const defaultDarkColorScale2 = invertColorScale(defaultColorScale2);
function defaultDivergingDarkColorScale(v) {
    if (v >= 0.5) {
        return defaultDarkColorScale((v - 0.5) * 2);
    }
    return defaultDarkColorScale2(1 - v * 2);
}
function autoAssignColors(colors, start = 0) {
    let i = start;
    const map = new Map();
    return (c) => {
        const elem = map.get(c);
        if (elem) {
            return elem;
        }
        const color = colors[i % colors.length];
        i += 1;
        map.set(c, color);
        return color;
    };
}
const schemeTableau10 = [
    '#4e79a7',
    '#e15759',
    '#76b7b2',
    '#59a14f',
    '#edc949',
    '#af7aa1',
    '#ff9da7',
    '#9c755f',
    '#bab0ab',
    '#f28e2c',
];
function defaultCategoricalColorScale() {
    return autoAssignColors(schemeTableau10, 0);
}

function toHistString(hist) {
    return `(${hist.map((d) => `${d.label}=${d.count}`).join(', ')})`;
}
function maxHistBin(hist) {
    if (hist.length === 0) {
        return null;
    }
    let max = hist[0];
    for (const bin of hist) {
        if (bin.count > max.count) {
            max = bin;
        }
    }
    return max;
}

function isCategoricalStats(obj) {
    return (obj != null &&
        Array.isArray(obj.categories) &&
        Array.isArray(obj.hist));
}
function formatCategories(...categoryLabel) {
    const map = new Map(categoryLabel);
    return (v) => { var _a; return (_a = map.get(v)) !== null && _a !== void 0 ? _a : v; };
}
function toCategoricalString() {
    return `CategoricalStats(count=${this.count}, hist=${toHistString(this.hist)})`;
}
function toCategoricalPrimitive(hint) {
    if (hint === 'string' || hint === 'default') {
        return toCategoricalString.call(this);
    }
    if (hint === 'number') {
        return this.maxBin.count;
    }
    throw new TypeError('invalid hint');
}
function categoricalStatsGenerator(options = {}) {
    var _a, _b;
    const color = (_a = options.color) !== null && _a !== void 0 ? _a : defaultCategoricalColorScale();
    const format = (_b = options.format) !== null && _b !== void 0 ? _b : ((v) => v);
    return (arr) => {
        var _a;
        const map = new Map();
        if (options.categories) {
            options.categories.forEach((cat) => map.set(cat, 0));
        }
        let missing = 0;
        const items = [];
        let flatMissing = 0;
        const flatItems = [];
        const pushValue = (v) => {
            var _a;
            flatItems.push(v);
            map.set(v, 1 + ((_a = map.get(v)) !== null && _a !== void 0 ? _a : 0));
        };
        for (const v of arr) {
            if (v == null) {
                missing += 1;
                flatMissing += 1;
                continue;
            }
            if (typeof v === 'string' || (!Array.isArray(v) && !(v instanceof Set))) {
                const vs = `${v}`;
                items.push(vs);
                pushValue(vs);
                continue;
            }
            if (v instanceof Set) {
                items.push(v);
                v.forEach((vi) => pushValue(`${vi}`));
                continue;
            }
            if (!Array.isArray(v)) {
                continue;
            }
            const vClean = [];
            for (const vi of v) {
                if (vi == null) {
                    flatMissing += 1;
                }
                else {
                    const vs = `${vi}`;
                    pushValue(vs);
                    vClean.push(vs);
                }
            }
            if (vClean.length === v.length) {
                items.push(v);
            }
            else {
                items.push(vClean);
            }
        }
        const entries = options.categories
            ? options.categories.map((c) => { var _a; return [c, (_a = map.get(c)) !== null && _a !== void 0 ? _a : 0]; })
            : Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
        const hist = entries.map(([value, count]) => ({
            x0: value,
            x1: value,
            label: format(value),
            count,
            items: Array(count).fill(value),
            color: color(value),
        }));
        const r = {
            categories: (_a = options.categories) !== null && _a !== void 0 ? _a : hist.map((d) => d.x0),
            hist,
            items,
            maxBin: maxHistBin(hist),
            color,
            format,
            missing,
            count: arr.length,
            flatMissing,
            flatItems,
            flatCount: flatItems.length + flatMissing,
            [Symbol.toPrimitive]: toCategoricalPrimitive,
        };
        return r;
    };
}

function isDateStats(obj) {
    return (obj != null && typeof obj.histGranularity === 'string' && Array.isArray(obj.hist));
}
function toDateString() {
    const f = (v) => this.format(v);
    return `DateStats(count=${this.count}, min=${f(this.min)}, max=${f(this.max)}, hist=${toHistString(this.hist)})`;
}
function toDatePrimitive(hint) {
    if (hint === 'string' || hint === 'default') {
        return toDateString.call(this);
    }
    if (hint === 'number') {
        return this.median.valueOf();
    }
    throw new TypeError('invalid hint');
}
function computeGranularity(min, max, color, format, histGranularity) {
    const hist = [];
    if (histGranularity === 'decade' || max.getFullYear() - min.getFullYear() >= 30) {
        const formatter = yearFormatter(format);
        const minYear = Math.floor(min.getFullYear() / 10) * 10;
        const maxYear = max.getFullYear();
        for (let i = minYear; i <= maxYear; i += 10) {
            const x0 = new Date(i, 0, 1);
            hist.push({
                x0,
                x1: new Date(i + 10, 0, 1),
                label: formatter(x0),
                color: color(new Date(i + 5, 0, 1)),
                items: [],
                count: 0,
            });
        }
        return { hist, histGranularity: 'decade' };
    }
    if (histGranularity === 'year' || max.getFullYear() - min.getFullYear() >= 2) {
        const formatter = yearFormatter(format);
        const minYear = min.getFullYear();
        const maxYear = max.getFullYear();
        for (let i = minYear; i <= maxYear; i += 1) {
            const x0 = new Date(i, 0, 1);
            hist.push({
                x0,
                x1: new Date(i + 1, 0, 1),
                label: formatter(x0),
                color: color(new Date(i, 6, 1)),
                items: [],
                count: 0,
            });
        }
        return { hist, histGranularity: 'year' };
    }
    if (histGranularity === 'day' || max.getTime() - min.getTime() <= 1000 * 60 * 60 * 24 * 31) {
        let x0 = new Date(min.getFullYear(), min.getMonth(), min.getDate());
        const formatter = resolveDateFormatter(format);
        while (x0 <= max) {
            const x1 = new Date(x0);
            x1.setDate(x1.getDate() + 1);
            hist.push({
                x0,
                x1,
                label: formatter(x0),
                color: color(new Date(x0.getFullYear(), x0.getMonth(), x0.getDate(), 12)),
                items: [],
                count: 0,
            });
            x0 = x1;
        }
        return { hist, histGranularity: 'day' };
    }
    let x0 = new Date(min.getFullYear(), min.getMonth(), 1);
    const formatter = monthFormatter(format);
    while (x0 <= max) {
        const x1 = new Date(x0);
        x1.setMonth(x1.getMonth() + 1);
        hist.push({
            x0,
            x1,
            label: formatter(x0),
            color: color(new Date(x0.getFullYear(), x0.getMonth(), 15)),
            items: [],
            count: 0,
        });
        x0 = x1;
    }
    return { hist, histGranularity: 'month' };
}
function pushDateHist(hist, v, count = 1) {
    const values = Array(count).fill(v);
    if (v < hist[0].x1) {
        hist[0].count += count;
        hist[0].items.push(...values);
        return;
    }
    const l = hist.length - 1;
    if (v > hist[l].x0) {
        hist[l].count += count;
        hist[l].items.push(...values);
        return;
    }
    if (l === 2) {
        hist[1].count += count;
        hist[1].items.push(...values);
        return;
    }
    let low = 1;
    let high = l;
    while (low < high) {
        const center = Math.floor((high + low) / 2);
        if (v < hist[center].x1) {
            high = center;
        }
        else {
            low = center + 1;
        }
    }
    hist[low].count += count;
    hist[low].items.push(...values);
}
function resolveDateFormatter(format) {
    if (typeof format === 'function') {
        return format;
    }
    const f = new Intl.DateTimeFormat(format === null || format === void 0 ? void 0 : format.locales, format === null || format === void 0 ? void 0 : format.options);
    return f.format.bind(f);
}
function yearFormatter(format) {
    var _a;
    if (typeof format === 'function') {
        return format;
    }
    const f = new Intl.DateTimeFormat(format === null || format === void 0 ? void 0 : format.locales, {
        year: 'numeric',
        ...((_a = format === null || format === void 0 ? void 0 : format.options) !== null && _a !== void 0 ? _a : {}),
    });
    return f.format.bind(f);
}
function monthFormatter(format) {
    var _a;
    if (typeof format === 'function') {
        return format;
    }
    const f = new Intl.DateTimeFormat(format === null || format === void 0 ? void 0 : format.locales, {
        year: 'numeric',
        month: 'short',
        ...((_a = format === null || format === void 0 ? void 0 : format.options) !== null && _a !== void 0 ? _a : {}),
    });
    return f.format.bind(f);
}
function normalizeDate(min, max) {
    const minN = min.getTime();
    const maxN = max.getTime();
    const range = maxN - minN;
    return {
        min,
        max,
        scale: (v) => (range === 0 ? 0.5 : (v.getTime() - minN) / range),
        invert: (v) => (range === 0 ? min : new Date(v * range + minN)),
    };
}
function createFlat$1(arr) {
    let missing = 0;
    const items = [];
    let flatMissing = 0;
    const flatItems = [];
    let min = Number.POSITIVE_INFINITY;
    let max = Number.NEGATIVE_INFINITY;
    const push = (v) => {
        flatItems.push(v);
        const vt = v.getTime();
        if (vt < min) {
            min = vt;
        }
        if (vt > max) {
            max = vt;
        }
    };
    for (const v of arr) {
        if (v == null) {
            missing += 1;
            flatMissing += 1;
            continue;
        }
        if (v instanceof Date) {
            items.push(v);
            push(v);
            continue;
        }
        if (v instanceof Set) {
            items.push(v);
            v.forEach((vi) => push(vi));
            continue;
        }
        if (!Array.isArray(v)) {
            continue;
        }
        const vClean = [];
        for (const vi of v) {
            if (vi == null || !(vi instanceof Date)) {
                flatMissing += 1;
            }
            else {
                push(vi);
                vClean.push(vi);
            }
        }
        if (vClean.length === v.length) {
            items.push(v);
        }
        else {
            items.push(vClean);
        }
    }
    return {
        missing,
        items,
        flatMissing,
        flatItems,
        min,
        max,
    };
}
function computeMedian(items) {
    if (items.length === 0) {
        return new Date();
    }
    const sorted = items.slice().sort((a, b) => a.valueOf() - b.valueOf());
    if (items.length % 2 === 1) {
        return sorted[(items.length + 1) / 2];
    }
    const i = items.length / 2;
    const a = sorted[i];
    const b = sorted[i + 1];
    return new Date((a.valueOf() + b.valueOf()) / 2);
}
function dateStatsGenerator(options = {}) {
    var _a;
    const format = resolveDateFormatter(options.format);
    const color = (_a = options.color) !== null && _a !== void 0 ? _a : defaultConstantColorScale;
    return (arr) => {
        var _a, _b;
        const base = {
            color,
            format,
            count: arr.length,
        };
        const { missing, items, flatMissing, flatItems, min, max } = createFlat$1(arr);
        const now = new Date();
        const minD = (_a = options.min) !== null && _a !== void 0 ? _a : (Number.isFinite(min) ? new Date(min) : now);
        const maxD = (_b = options.max) !== null && _b !== void 0 ? _b : (Number.isFinite(max) ? new Date(max) : now);
        const { hist, histGranularity } = computeGranularity(minD, maxD, color, options.format, options.histGranularity);
        for (const v of flatItems) {
            pushDateHist(hist, v);
        }
        const r = {
            missing,
            items,
            flatMissing,
            flatItems,
            flatCount: flatMissing + flatItems.length,
            hist,
            histGranularity,
            median: computeMedian(flatItems),
            maxBin: maxHistBin(hist),
            ...normalizeDate(minD, maxD),
            ...base,
            [Symbol.toPrimitive]: toDatePrimitive,
        };
        return r;
    };
}

function normalize(min, max) {
    const range = max - min;
    return (v) => (range === 0 ? 0.5 : (v - min) / range);
}
function deNormalize(min, max) {
    const range = max - min;
    return (v) => v * range + min;
}

function getNumberOfBins(length) {
    if (length === 0) {
        return 1;
    }
    return Math.ceil(Math.log(length) / Math.LN2) + 1;
}
function isNumberStats(obj) {
    return obj != null && typeof obj.min === 'number' && typeof obj.max === 'number';
}
function toNumberString() {
    const f = (v) => this.format(v);
    return `NumberStats(count=${this.count}, min=${f(this.min)}, median=${f(this.median)}, max=${f(this.max)}, hist=${toHistString(this.hist)})`;
}
function toNumberPrimitive(hint) {
    if (hint === 'string' || hint === 'default') {
        return toNumberString.call(this);
    }
    if (hint === 'number') {
        return this.median;
    }
    throw new TypeError('invalid hint');
}
function resolveNumberFormatter(format) {
    if (typeof format === 'function') {
        return format;
    }
    const f = new Intl.NumberFormat(format ? format.locales : undefined, format ? format.options : undefined);
    return (v) => (v == null ? '' : f.format(v));
}
function createHist(b, min, max, color, format, numberOfBins) {
    const bins = numberOfBins !== null && numberOfBins !== void 0 ? numberOfBins : getNumberOfBins(b.count);
    const binWidth = (max - min) / bins;
    const scale = normalize(min, max);
    const hist = Array(bins)
        .fill(0)
        .map((_, i) => {
        const x0 = min + binWidth * i;
        return {
            count: 0,
            items: [],
            x0,
            x1: x0 + binWidth,
            label: `${format(x0)} - ${format(x0 + binWidth)}`,
            color: color(scale(x0 + binWidth / 2)),
        };
    });
    if (hist.length === 0) {
        return hist;
    }
    hist[hist.length - 1].x1 = max;
    for (let i = 0; i < b.items.length; i += 1) {
        const v = +b.items[i];
        const bin = hist.find((d) => v < d.x1) || hist[hist.length - 1];
        bin.count += 1;
        bin.items.push(v);
    }
    return hist;
}
function createFlat(arr) {
    let missing = 0;
    const items = [];
    let flatMissing = 0;
    const flatItems = [];
    for (const v of arr) {
        if (v == null) {
            missing += 1;
            flatMissing += 1;
            continue;
        }
        if (typeof v === 'number' || (!Array.isArray(v) && !(v instanceof Set))) {
            items.push(+v);
            flatItems.push(+v);
            continue;
        }
        if (v instanceof Set) {
            items.push(v);
            v.forEach((vi) => flatItems.push(+vi));
            continue;
        }
        if (!Array.isArray(v)) {
            continue;
        }
        const vClean = [];
        for (const vi of v) {
            if (vi == null) {
                flatMissing += 1;
            }
            else {
                flatItems.push(+vi);
                vClean.push(+vi);
            }
        }
        if (vClean.length === v.length) {
            items.push(v);
        }
        else {
            items.push(vClean);
        }
    }
    return {
        missing,
        items,
        flatMissing,
        flatItems,
    };
}
function numberStatsGenerator(options = {}) {
    const format = resolveNumberFormatter(options.format);
    return (arr) => {
        var _a, _b, _c;
        const { missing, items, flatMissing, flatItems } = createFlat(arr);
        const b = boxplot(flatItems, options);
        const min = (_a = options.min) !== null && _a !== void 0 ? _a : b.min;
        const max = (_b = options.max) !== null && _b !== void 0 ? _b : b.max;
        const color = (_c = options.color) !== null && _c !== void 0 ? _c : (min < 0 && max > 0 ? defaultDivergingColorScale : defaultColorScale);
        const hist = createHist(b, min, max, color, format, options.numberOfBins);
        const r = Object.assign(b, {
            min,
            max,
            center: options.center,
            hist,
            missing,
            items,
            flatMissing,
            flatItems,
            flatCount: flatMissing + flatItems.length,
            maxBin: maxHistBin(hist),
            scale: normalize(min, max),
            invert: deNormalize(min, max),
            format,
            color,
            [Symbol.toPrimitive]: toNumberPrimitive,
        });
        return r;
    };
}

function isTextStats(obj) {
    return (obj != null && typeof obj.unique === 'number' && Array.isArray(obj.mostFrequent));
}
function toTextString() {
    return `TextStats(count=${this.count},mostFrequent=(${this.mostFrequent
        .map((d) => `${d.value}=${d.count}`)
        .join(', ')}))`;
}
function toTextPrimitive(hint) {
    var _a, _b;
    if (hint === 'string' || hint === 'default') {
        return toTextString.call(this);
    }
    if (hint === 'number') {
        return (_b = (_a = this.mostFrequent[0]) === null || _a === void 0 ? void 0 : _a.count) !== null && _b !== void 0 ? _b : this.unique;
    }
    throw new TypeError('invalid hint');
}
function textStatsGenerator(options = {}) {
    var _a, _b;
    const mostFrequentCount = (_a = options.mostFrequentCount) !== null && _a !== void 0 ? _a : 10;
    const format = (_b = options.format) !== null && _b !== void 0 ? _b : ((v) => (v ? v.toString() : v));
    return (arr) => {
        let missing = 0;
        const counter = new Map();
        const items = [];
        arr.forEach((v) => {
            if (!v) {
                missing += 1;
            }
            else {
                const c = counter.get(v);
                if (c) {
                    c.count += 1;
                    items.push(v);
                }
                else {
                    counter.set(v, { value: v, count: 1 });
                    items.push(v);
                }
            }
        });
        const r = {
            unique: counter.size,
            items,
            mostFrequent: mostFrequentCount <= 0
                ? []
                : Array.from(counter.values())
                    .sort((a, b) => (a.count !== b.count ? b.count - a.count : a.value.localeCompare(b.value)))
                    .slice(0, mostFrequentCount),
            missing,
            count: arr.length,
            format,
            flatCount: arr.length,
            flatItems: items,
            flatMissing: missing,
            [Symbol.toPrimitive]: toTextPrimitive,
        };
        r.toString = toTextString;
        return r;
    };
}

function barProps(value, color) {
    if (value == null || Number.isNaN(value) || value <= 0) {
        return null;
    }
    const c = typeof color === 'string' ? color : color(value);
    if (value >= 1) {
        return {
            backgroundColor: c,
        };
    }
    const p = toPercent(value);
    return {
        backgroundImage: `linear-gradient(to right, ${c} ${p}, transparent ${p})`,
    };
}
function NumberBar(props) {
    var _a, _b;
    const label = format(props.value, (_a = props.format) !== null && _a !== void 0 ? _a : toLocaleString);
    return (React.createElement("div", { className: clsx('lt-bar', props.className), style: mergeStyles(props.style, barProps(props.scale ? props.scale(props.value) : props.value, (_b = props.color) !== null && _b !== void 0 ? _b : defaultColorScale)), title: label }, label));
}
function divergingBarProps(center, value, color) {
    if (value == null || Number.isNaN(value) || value === center) {
        return null;
    }
    const c = typeof color === 'string' ? color : color(value);
    const p = toPercent(value);
    const m = toPercent(center);
    if (value <= 0) {
        return {
            backgroundImage: `linear-gradient(to right, ${c} ${m}, transparent ${m})`,
        };
    }
    if (value >= 1) {
        return {
            backgroundImage: `linear-gradient(to right, transparent ${m}, ${c} ${m})`,
        };
    }
    if (value < center) {
        return {
            backgroundImage: `linear-gradient(to right, transparent ${p}, ${c} ${p}, ${c} ${m}, transparent ${m})`,
        };
    }
    return {
        backgroundImage: `linear-gradient(to right, transparent ${m}, ${c} ${m}, ${c} ${p}, transparent ${p})`,
    };
}
function DivergingNumberBar(props) {
    var _a, _b, _c, _d;
    const label = format(props.value, (_a = props.format) !== null && _a !== void 0 ? _a : toLocaleString);
    return (React.createElement("div", { className: clsx('lt-diverging-bar', props.className), style: mergeStyles(props.style, divergingBarProps(props.scale ? props.scale((_b = props.center) !== null && _b !== void 0 ? _b : 0) : (_c = props.center) !== null && _c !== void 0 ? _c : 0.5, props.scale ? props.scale(props.value) : props.value, (_d = props.color) !== null && _d !== void 0 ? _d : defaultDivergingColorScale)), title: label }, label));
}

function NumberColor(props) {
    var _a, _b;
    const v = props.scale && props.value != null && !Number.isNaN(props.value) ? props.scale(props.value) : props.value;
    const label = format(props.value, (_a = props.format) !== null && _a !== void 0 ? _a : toLocaleString);
    return (React.createElement("div", { className: clsx('lt-color', props.className), style: mergeStyles(props.style, props.value != null &&
            !Number.isNaN(props.value) && {
            borderLeftColor: typeof props.color === 'string' ? props.color : ((_b = props.color) !== null && _b !== void 0 ? _b : defaultColorScale)(v),
        }), title: label }, label));
}

function radiusFromArea(area) {
    return Math.sqrt(area);
}
function proportionalSymbolProps(value, color) {
    const c = typeof color === 'string' ? color : color(value);
    const v = radiusFromArea(value);
    const p = toPercent(v);
    const p2 = toPercent(Math.min(1, v + 0.04));
    return value != null && !Number.isNaN(value) && value > 0
        ? {
            backgroundImage: `radial-gradient(circle closest-side at 0.6em 50%, ${c} ${p}, transparent ${p2})`,
        }
        : null;
}
function NumberSymbol(props) {
    var _a, _b;
    const label = format(props.value, (_a = props.format) !== null && _a !== void 0 ? _a : toLocaleString);
    return (React.createElement("div", { className: clsx('lt-proportional-symbol', props.className), style: mergeStyles(props.style, proportionalSymbolProps(props.scale ? props.scale(props.value) : props.value, (_b = props.color) !== null && _b !== void 0 ? _b : defaultColorScale)), title: label }, label));
}

const TEXT_SUMMARY_I18N_EN = {
    textSummaryItems: '{0} items',
    textSummaryUniqueItems: '{0} unique',
    filterTextPlaceholder: 'Filter {0} unique items',
};
function TextSummary(props) {
    const { s } = props;
    const i18n = useI18N(TEXT_SUMMARY_I18N_EN, props.i18n);
    return (React.createElement("div", { className: clsx('lt-text-summary', !props.summary && 'lt-group', props.className), style: props.style },
        React.createElement("span", null, i18n.textSummaryItems(s.count.toLocaleString())),
        s.unique < s.count && React.createElement("span", null, i18n.textSummaryUniqueItems(s.unique.toLocaleString()))));
}
function FilterTextSummary(props) {
    const { setFilter, filterValue, s, preFilter } = props;
    const unique = `${s.count.toLocaleString()}${preFilter ? `/${preFilter.count.toLocaleString()}` : ''} items`;
    const onChange = useCallback((evt) => {
        setFilter(evt.currentTarget.value || undefined);
    }, [setFilter]);
    const clearFilter = useCallback(() => setFilter(undefined), [setFilter]);
    const i18n = useI18N(TEXT_SUMMARY_I18N_EN, props.i18n);
    return (React.createElement("div", { className: clsx('lt-text-summary', 'lt-summary', props.className), "data-min": unique, style: props.style, "data-filtered": filterValue ? 'true' : undefined },
        React.createElement("input", { value: filterValue !== null && filterValue !== void 0 ? filterValue : '', onChange: onChange, placeholder: props.placeholder ? props.placeholder(s) : i18n.filterTextPlaceholder(s.unique.toLocaleString()), size: 3, className: "lt-text-summary-input" }),
        React.createElement("button", { type: "button", className: "lt-text-summary-clear", "aria-label": "clear filter", disabled: !filterValue, onClick: clearFilter }, "\u00D7")));
}

function TextLabel(props) {
    const label = format(props.value, props.format);
    return (React.createElement("div", { className: clsx('lt-text', props.className), style: props.style, title: label }, label));
}

function UpSetDot(props) {
    const style = props.color ? { fill: props.color } : undefined;
    return (React.createElement("svg", { viewBox: "0 0 2 2", className: props.className, style: props.style },
        React.createElement("title", null, props.title),
        React.createElement("circle", { className: clsx('lt-upset-dot', props.mode === true && 'lt-upset-dot-enabled'), r: "1", cx: "1", cy: "1", style: style }),
        props.mode == null && (React.createElement("circle", { className: clsx('lt-upset-dot', 'lt-upset-dot-enabled'), r: "0.5", cx: "1", cy: "1", style: style }))));
}

const UPSET_LINE_I18N_EN = {
    upsetHas: '{0}: yes',
    upsetHasAll: '{0}: all',
    upsetHasSome: '{0}: some',
    upsetHasNone: '{0}: none',
    upsetHasNot: '{0}: no',
};
function isCategoricalSetValue(v) {
    return Array.isArray(v) || v instanceof Set;
}
function categoricalSetDegree(v) {
    if (v == null) {
        return 0;
    }
    return v instanceof Set ? v.size : v.length;
}
function generateGradient(colors, x1, x2) {
    const id = `upset-line-g-${colors.join(',')}`.replace(/[, ]+/gm, '').replace(/[-#$()[\]{}"']+/gm, '-');
    return {
        stroke: `url('#${id}')`,
        def: (React.createElement("defs", null,
            React.createElement("linearGradient", { id: id, x1: x1, x2: x2, gradientUnits: "userSpaceOnUse" }, colors.map((d, i) => (React.createElement("stop", { key: d, offset: toPercent(i / (colors.length - 1)), stopColor: d })))))),
    };
}
function UpSetLineLine({ first, last, sets, color, }) {
    if (first >= last) {
        return React.createElement(React.Fragment, null);
    }
    let stroke;
    let g = null;
    if (typeof color === 'string') {
        stroke = color;
    }
    else if (typeof color === 'function') {
        const colors = sets.slice(first, last + 1).map((d) => color(d));
        if (colors.every((d) => d === colors[0])) {
            stroke = colors[0];
        }
        else {
            const r = generateGradient(colors, first * 2, last * 2);
            stroke = r.stroke;
            g = r.def;
        }
    }
    return (React.createElement("svg", { className: "lt-upset-line-line", viewBox: `0 0 ${(sets.length - 1) * 2} 2`, preserveAspectRatio: "none", style: { left: toPercent(1 / (sets.length + 1)), width: toPercent(1 - 2 / (sets.length + 1)) } },
        g,
        React.createElement("path", { d: `M${first * 2},1 L${last * 2},1.001`, style: stroke ? { stroke } : undefined })));
}
function UpSetLine(props) {
    const has = props.value instanceof Set ? props.value : new Set(props.value);
    const { sets } = props;
    const first = sets.findIndex((d) => has.has(d));
    let last = first;
    if (has.size > 1 && first < sets.length - 1) {
        last =
            sets.length -
                1 -
                sets
                    .slice()
                    .reverse()
                    .findIndex((d) => has.has(d));
    }
    const i18n = useI18N(UPSET_LINE_I18N_EN, props.i18n);
    return (React.createElement("div", { className: clsx('lt-upset-line', props.className), style: props.style }, props.value != null && (React.createElement(React.Fragment, null,
        sets.map((s) => (React.createElement(UpSetDot, { key: s, mode: has.has(s), title: (has.has(s) ? i18n.upsetHas : i18n.upsetHasNot)(format(s, props.format)), color: typeof props.color === 'string' ? props.color : props.color ? props.color(s) : undefined }))),
        React.createElement(UpSetLineLine, { first: first, last: last, sets: sets, color: props.color })))));
}
function UpSetPartialLine(props) {
    const i18n = useI18N(UPSET_LINE_I18N_EN, props.i18n);
    return (React.createElement("div", { className: clsx('lt-upset-line', props.className), style: props.style }, props.sets.map((s, i) => {
        const has = props.value[i] === true;
        const hasNot = props.value[i] === false;
        const partial = !has && !hasNot;
        return (React.createElement(UpSetDot, { key: s, mode: !has && !hasNot ? null : has, title: (has ? i18n.upsetHasAll : partial ? i18n.upsetHasSome : i18n.upsetHasNone)(format(s, props.format)), color: typeof props.color === 'string' ? props.color : props.color ? props.color(s) : undefined }));
    })));
}

function HeatMap1D(props) {
    var _a;
    return (React.createElement("div", { className: clsx('lt-heatmap-1d', props.className), style: props.style, title: typeof props.format === 'string' ? props.format : undefined }, ((_a = props.value) !== null && _a !== void 0 ? _a : []).map((v, i) => {
        var _a;
        const label = typeof props.format === 'function' ? props.format(v, i) : toLocaleString(v);
        const normalized = typeof props.scale === 'function' && v != null ? props.scale(v) : v;
        const color = typeof props.color === 'string' ? props.color : ((_a = props.color) !== null && _a !== void 0 ? _a : defaultColorScale)(normalized, i);
        return (React.createElement("div", { key: i, className: "lt-heatmap-1d-cell", title: label, style: { backgroundColor: color } },
            React.createElement("span", { "aria-hidden": "false" }, label)));
    })));
}

function BoxPlotArray(props) {
    var _a, _b;
    const f = props.format;
    const c = props.color;
    const scale = (_a = props.scale) !== null && _a !== void 0 ? _a : defaultScale;
    return (React.createElement("div", { className: clsx('lt-heatmap-1d', props.className), style: props.style, title: typeof props.format === 'string' ? props.format : undefined }, ((_b = props.value) !== null && _b !== void 0 ? _b : []).map((v, i) => {
        const s = {
            ...v,
            format: typeof f !== 'function' ? toLocaleString : (d) => f(d, i),
            color: typeof c === 'function' ? (d) => c(d, i) : defaultColorScale,
            scale,
        };
        return React.createElement(BoxPlotChartVertical, { className: "lt-heatmap-1d-cell", key: i, s: s, i18n: props.i18n });
    })));
}

function toStackedValuePrimitive(hint) {
    if (hint === 'string' || hint === 'default') {
        return this.value.toString();
    }
    if (hint === 'number') {
        return this.value;
    }
    throw new TypeError('invalid hint');
}
function asStackedValue(value, stack) {
    return {
        value,
        stack,
        valueOf: () => value,
        toString: () => value.toString(),
        [Symbol.toPrimitive]: toStackedValuePrimitive,
    };
}
function computeWeightedSum(stack, colors) {
    const weights = stack.map((v) => { var _a; return (typeof v === 'number' ? 1 : (_a = v.weight) !== null && _a !== void 0 ? _a : 1); });
    const cs = resolveColor(stack, colors);
    const weightSum = weights.reduce((acc, w) => acc + w, 0);
    const vs = stack.map((v) => (typeof v === 'number' ? v : v.value));
    const value = vs.reduce((acc, v, i) => acc + v * weights[i], 0) / weightSum;
    const ratios = stack.map((v, i) => ({
        color: cs[i],
        ratio: ((typeof v === 'number' ? v : v.value) * weights[i]) / (value * weightSum),
    }));
    return asStackedValue(value, ratios);
}
function resolveColor(stack, colors) {
    const cs = colors !== null && colors !== void 0 ? colors : schemeTableau10;
    return stack.map((v, i) => {
        if (typeof v !== 'number' && typeof v.color === 'string') {
            return v.color;
        }
        return cs[i];
    });
}
function computeWeightedSumFactory(stack, colors) {
    const weights = stack.map((v) => (typeof v === 'number' ? v : v.weight), 0);
    const cs = resolveColor(stack, colors);
    const weightSum = weights.reduce((acc, w) => acc + w, 0);
    return (vs) => {
        const value = weights.reduce((acc, w, i) => acc + vs[i] * w, 0) / weightSum;
        const ratios = weights.map((w, i) => ({ color: cs[i], ratio: (vs[i] * w) / (value * weightSum) }));
        return asStackedValue(value, ratios);
    };
}
function stackedBarProps(value, stack) {
    if (value == null || Number.isNaN(value) || value <= 0) {
        return null;
    }
    const p = toPercent(value);
    const preSum = stack.stack.reduce((acc, v) => {
        acc.push(acc[acc.length - 1] + v.ratio);
        return acc;
    }, [0]);
    const parts = stack.stack
        .map((w, i) => {
        const pi = toPercent(preSum[i] * value);
        const pi2 = toPercent((preSum[i] + w.ratio) * value);
        if (pi === pi2) {
            return '';
        }
        return i === 0 ? `${w.color} ${pi2}` : `${w.color} ${pi}, ${w.color} ${pi2}`;
    })
        .filter(Boolean)
        .join(', ');
    return {
        backgroundImage: `linear-gradient(to right, ${parts}, transparent ${p})`,
    };
}
function StackedBar(props) {
    var _a, _b;
    const label = format(+props.value, (_a = props.format) !== null && _a !== void 0 ? _a : toLocaleString);
    const scale = (_b = props.scale) !== null && _b !== void 0 ? _b : defaultScale;
    return (React.createElement("div", { className: clsx('lt-bar', props.className), style: mergeStyles(props.style, stackedBarProps(scale(+props.value), props.value)), title: label }, label));
}

const ALWAYS_TRUE = () => true;

function filterByCategoricalSetFilter(filterValue) {
    if (filterValue == null) {
        return ALWAYS_TRUE;
    }
    const must = [];
    const mustNot = [];
    for (const v of filterValue) {
        if (v.value) {
            must.push(v.set);
        }
        else {
            mustNot.push(v.set);
        }
    }
    return (v) => {
        const hasV = v instanceof Set ? v : new Set(v);
        if (mustNot.length > 0) {
            for (const mi of mustNot) {
                if (hasV.has(mi)) {
                    return false;
                }
            }
        }
        if (must.length > 0) {
            for (const mi of must) {
                if (!hasV.has(mi)) {
                    return false;
                }
            }
        }
        return true;
    };
}

function filterByRangeFilter(filterValue) {
    if (filterValue == null) {
        return ALWAYS_TRUE;
    }
    const matches = (v) => (filterValue[0] == null || v >= filterValue[0]) && (filterValue[1] == null || v <= filterValue[1]);
    return (v) => {
        if (v == null) {
            return false;
        }
        if (Array.isArray(v)) {
            return v.every((vi) => v != null && matches(vi));
        }
        return matches(v);
    };
}

function filterByCategoricalFilter(filterValue) {
    if (filterValue == null) {
        return ALWAYS_TRUE;
    }
    const lookup = new Set(filterValue);
    return (v) => {
        if (v == null) {
            return false;
        }
        return !lookup.has(v);
    };
}

export { ALWAYS_TRUE, BOXPLOT_I18N_EN, BoxPlot, BoxPlotArray, BoxPlotChart, BoxPlotChartVertical, CategoricalColor, DateLabel, DivergingNumberBar, FILTER_BIN_I18N_EN, FILTER_RANGE_I18N_EN, FILTER_SET_I18N_EN, FilterBinHistogram, FilterRangeBoxPlot, FilterRangeHistogram, FilterRangeWrapper, FilterSetHistogram, FilterTextSummary, HeatMap1D, Histogram, NumberBar, NumberColor, NumberSymbol, StackedBar, TEXT_SUMMARY_I18N_EN, TextLabel, TextSummary, UPSET_LINE_I18N_EN, UpSetLine, UpSetPartialLine, asStackedValue, autoAssignColors, categoricalSetDegree, categoricalStatsGenerator, clsx, computeWeightedSum, computeWeightedSumFactory, dateStatsGenerator, deNormalize, defaultCategoricalColorScale, defaultColorScale, defaultColorScale2, defaultConstantColorScale, defaultConstantDarkColorScale, defaultDarkColorScale, defaultDarkColorScale2, defaultDivergingColorScale, defaultDivergingDarkColorScale, defaultScale, filterByCategoricalFilter, filterByCategoricalSetFilter, filterByRangeFilter, format, formatCategories, getNumberOfBins, invertColorScale, isCategoricalSetValue, isCategoricalStats, isDateStats, isNumberStats, isTextStats, maxHistBin, mergeStyles, normalize, numberStatsGenerator, resolveDateFormatter, resolveNumberFormatter, schemeTableau10, textStatsGenerator, toHistString, toLocaleString, useI18N };
//# sourceMappingURL=index.js.map
